/**
 * Edit name — mirrors WordPress Account details:
 * First name + Last name on one row, Display name below.
 *
 * Writes Supabase user metadata only; a WP sync still has to be added later.
 * Title comes from the stack header.
 */
import {
  SettingsPanel,
  SettingsPanelBody,
} from '@/components/settings/SettingsPanel';
import Container from '@/components/ui/Container';
import { BodyText, CaptionText } from '@/components/ui/Typography';
import { useAuth } from '@/lib/AuthContext';
import { BRAND_COLORS as C } from '@/lib/brandColors';
import { CONTENT_MAX_WIDTH, useIsMobile } from '@/lib/useDeviceType';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { ScrollView } from 'react-native';
import { Button, Input, Spinner, useMedia, XStack, YStack } from 'tamagui';

function NameField({
  label,
  value,
  onChangeText,
  placeholder,
  required,
  hint,
}: {
  label: string;
  value: string;
  onChangeText: (t: string) => void;
  placeholder: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <YStack f={1} gap="$1.5" miw={0}>
      <XStack ai="center" gap={4}>
        <CaptionText color={C.muted} ta="left">
          {label}
        </CaptionText>
        {required ? (
          <CaptionText color={C.accent} ta="left">
            *
          </CaptionText>
        ) : null}
      </XStack>
      <Input
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        autoCapitalize="words"
        bg={C.cardBg}
        borderColor={C.border}
        borderWidth={1}
        br={10}
        h={46}
        color={C.text}
        focusStyle={{ borderColor: C.accent }}
      />
      {hint ? (
        <CaptionText color={C.muted} fontStyle="italic" ta="left">
          {hint}
        </CaptionText>
      ) : null}
    </YStack>
  );
}

export default function EditProfileScreen() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const media = useMedia();
  const sideBySide = media.gtSm;
  const { user, updateProfile } = useAuth();

  const metadata = user?.user_metadata ?? {};
  const initialFirst = metadata.first_name ?? '';
  const initialLast = metadata.last_name ?? '';
  const initialDisplay =
    metadata.display_name ??
    metadata.full_name ??
    [initialFirst, initialLast].filter(Boolean).join(' ') ??
    '';

  // Seeded once per mount: going back pops this screen, so reopening it always
  // re-reads the freshly saved metadata.
  const [firstName, setFirstName] = useState<string>(initialFirst);
  const [lastName, setLastName] = useState<string>(initialLast);
  const [displayName, setDisplayName] = useState<string>(initialDisplay);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goBack = () =>
    router.canGoBack() ? router.back() : router.replace('/settings');

  const dirty =
    firstName.trim() !== initialFirst.trim() ||
    lastName.trim() !== initialLast.trim() ||
    displayName.trim() !== String(initialDisplay).trim();

  const save = async () => {
    const first = firstName.trim();
    const last = lastName.trim();
    const display = displayName.trim();

    if (!first) {
      setError('First name is required.');
      return;
    }
    if (!last) {
      setError('Last name is required.');
      return;
    }
    if (!display) {
      setError('Display name is required.');
      return;
    }

    setSaving(true);
    setError(null);

    const { error: updateError } = await updateProfile({
      first_name: first,
      last_name: last,
      display_name: display,
      // Kept in sync for older call sites that still read full_name.
      full_name: display,
    });

    setSaving(false);

    if (updateError) {
      setError(
        updateError.message || 'Could not save your name. Please try again.',
      );
      return;
    }
    goBack();
  };

  return (
    <YStack f={1} bg={C.pageBg}>
      <ScrollView
        contentContainerStyle={{ paddingBottom: isMobile ? 40 : 56 }}
        showsVerticalScrollIndicator={false}
      >
        <Container
          maxWidth={CONTENT_MAX_WIDTH}
          centered
          py={isMobile ? '$4' : '$6'}
        >
          <YStack gap={isMobile ? '$3' : '$4'}>
            <SettingsPanel title="Account details">
              <SettingsPanelBody>
                {sideBySide ? (
                  <XStack gap="$3" w="100%">
                    <NameField
                      label="First name"
                      required
                      value={firstName}
                      onChangeText={setFirstName}
                      placeholder="First name"
                    />
                    <NameField
                      label="Last name"
                      required
                      value={lastName}
                      onChangeText={setLastName}
                      placeholder="Last name"
                    />
                  </XStack>
                ) : (
                  <>
                    <NameField
                      label="First name"
                      required
                      value={firstName}
                      onChangeText={setFirstName}
                      placeholder="First name"
                    />
                    <NameField
                      label="Last name"
                      required
                      value={lastName}
                      onChangeText={setLastName}
                      placeholder="Last name"
                    />
                  </>
                )}

                <NameField
                  label="Display name"
                  required
                  value={displayName}
                  onChangeText={setDisplayName}
                  placeholder="Display name"
                  hint="This will be how your name will be displayed in the account section and in reviews"
                />

                {error ? (
                  <BodyText color={C.error} ta="left">
                    {error}
                  </BodyText>
                ) : null}
              </SettingsPanelBody>
            </SettingsPanel>

            <XStack gap="$3" jc="flex-end" ai="center" flexWrap="wrap">
              <Button chromeless color={C.muted} onPress={goBack}>
                <CaptionText color={C.muted}>Cancel</CaptionText>
              </Button>
              <Button
                onPress={() => void save()}
                disabled={saving || !dirty}
                opacity={saving || !dirty ? 0.6 : 1}
                bg={C.accent}
                color={C.cardBg}
                fontWeight="700"
                br="$4"
                px="$5"
                h={44}
                icon={saving ? <Spinner color={C.cardBg} /> : undefined}
                pressStyle={{ opacity: 0.85 }}
                hoverStyle={{ opacity: 0.9 }}
              >
                Save
              </Button>
            </XStack>
          </YStack>
        </Container>
      </ScrollView>
    </YStack>
  );
}
